import * as anchor from "@project-serum/anchor";
import Big from "big.js";
export class AnchorWallet {
    constructor(payer) {
        this.payer = payer;
        this.payer = payer;
    }
    async signTransaction(tx) {
        tx.partialSign(this.payer);
        return tx;
    }
    async signAllTransactions(txs) {
        return txs.map((t) => {
            t.partialSign(this.payer);
            return t;
        });
    }
    get publicKey() {
        return this.payer.publicKey;
    }
}
/** A Switchboard V2 wrapper to assist in decoding onchain accounts */
export default class SwitchboardProgram {
    constructor(program, cluster) {
        this.program = program;
        this.cluster = cluster;
    }
    /**
     * Return the Switchboard devnet program
     * @param connection optional connection object if not using the default endpoints
     * @param confirmOptions optional confirmation options. defaults to commitment level 'confirmed'
     */
    static async loadDevnet(connection = new anchor.web3.Connection(anchor.web3.clusterApiUrl("devnet")), confirmOptions = SwitchboardProgram.defaultConfirmOptions) {
        const provider = new anchor.AnchorProvider(connection, new AnchorWallet(anchor.web3.Keypair.fromSeed(new Uint8Array(32).fill(1))), confirmOptions);
        const anchorIdl = await anchor.Program.fetchIdl(SwitchboardProgram.devnetPid, provider);
        if (!anchorIdl) {
            throw new Error(`failed to read devnet idl for ${SwitchboardProgram.devnetPid}`);
        }
        const program = new anchor.Program(anchorIdl, SwitchboardProgram.devnetPid, provider);
        return new SwitchboardProgram(program, "devnet");
    }
    /**
     * Return the Switchboard mainnet-beta program
     * @param connection optional connection object if not using the default endpoints
     * @param confirmOptions optional confirmation options. defaults to commitment level 'confirmed'
     */
    static async loadMainnet(connection = new anchor.web3.Connection(anchor.web3.clusterApiUrl("mainnet-beta")), confirmOptions = SwitchboardProgram.defaultConfirmOptions) {
        const provider = new anchor.AnchorProvider(connection, new AnchorWallet(anchor.web3.Keypair.fromSeed(new Uint8Array(32).fill(1))), confirmOptions);
        const anchorIdl = await anchor.Program.fetchIdl(SwitchboardProgram.mainnetPid, provider);
        if (!anchorIdl) {
            throw new Error(`failed to read devnet idl for ${SwitchboardProgram.mainnetPid}`);
        }
        const program = new anchor.Program(anchorIdl, SwitchboardProgram.mainnetPid, provider);
        return new SwitchboardProgram(program, "mainnet-beta");
    }
    /** Parse an aggregators account data and return the latest confirmed result if valid
     * @param aggregator an aggregators deserialized account data
     * @param maxStaleness the maximum duration in seconds before a result is considered invalid. Defaults to 0 which ignores any checks
     * @returns latest confirmed result as a big.js or null if the latest confirmed round has insufficient oracle responses or data is too stale
     */
    getLatestAggregatorValue(aggregator, maxStaleness = 0) {
        var _a, _b;
        if (((_b = (_a = aggregator.latestConfirmedRound) === null || _a === void 0 ? void 0 : _a.numSuccess) !== null && _b !== void 0 ? _b : 0) === 0) {
            return null;
        }
        if (maxStaleness !== 0) {
            const now = new anchor.BN(Date.now() / 1000);
            const latestRoundTimestamp = aggregator.latestConfirmedRound.roundOpenTimestamp;
            const staleness = now.sub(latestRoundTimestamp);
            if (staleness.gt(new anchor.BN(maxStaleness))) {
                return null;
            }
        }
        const mantissa = new Big(aggregator.latestConfirmedRound.result.mantissa.toString());
        const scale = aggregator.latestConfirmedRound.result.scale;
        const oldDp = Big.DP;
        Big.DP = 20;
        const result = mantissa.div(new Big(10).pow(scale));
        Big.DP = oldDp;
        return result;
    }
    /** Fetch and decode an aggregator account
     * @param aggregatorPubkey the aggregator's public key
     * @param commitment optional connection commitment level
     * @returns deserialized aggregator account, as specified by the Switchboard IDL
     */
    async fetchAggregator(aggregatorPubkey, commitment) {
        var _a;
        const aggregator = await ((_a = this.program.account.aggregatorAccountData) === null || _a === void 0 ? void 0 : _a.fetch(aggregatorPubkey, commitment));
        aggregator.ebuf = undefined;
        return aggregator;
    }
    /** Fetch and decode an aggregator's latest confirmed value if valid
     * @param aggregatorPubkey the aggregator's public key
     * @param commitment optional connection commitment level
     * @param maxStaleness the maximum duration in seconds before a result is considered invalid. Defaults to 0 which ignores any checks
     * @returns latest confirmed result as a big.js or null if the latest confirmed round has insufficient oracle responses or data is too stale
     */
    async fetchAggregatorLatestValue(aggregatorPubkey, commitment, maxStaleness = 0) {
        const aggregator = await this.fetchAggregator(aggregatorPubkey, commitment);
        return this.getLatestAggregatorValue(aggregator, maxStaleness);
    }
    /** Decode an aggregator's account info
     * @param accountInfo the aggregatror's account info
     * @returns deserialized aggregator account, as specified by the Switchboard IDL
     */
    decodeAggregator(accountInfo) {
        const coder = new anchor.BorshAccountsCoder(this.program.idl);
        const aggregator = coder.decode("AggregatorAccountData", accountInfo === null || accountInfo === void 0 ? void 0 : accountInfo.data);
        aggregator.ebuf = undefined;
        return aggregator;
    }
    /** Decode an aggregator and get the latest confirmed round
     * @param accountInfo the aggregator's account info
     * @param maxStaleness the maximum duration in seconds before a result is considered invalid. Defaults to 0 which ignores any checks
     * @returns latest confirmed result as a big.js or null if the latest confirmed round has insufficient oracle responses or data is too stale
     */
    decodeLatestAggregatorValue(accountInfo, maxStaleness = 0) {
        const aggregator = this.decodeAggregator(accountInfo);
        return this.getLatestAggregatorValue(aggregator, maxStaleness);
    }
}
/**
 * Switchboard Devnet Program ID
 * 2TfB33aLaneQb5TNVwyDz3jSZXS6jdW2ARw1Dgf84XCG
 */
SwitchboardProgram.devnetPid = new anchor.web3.PublicKey("2TfB33aLaneQb5TNVwyDz3jSZXS6jdW2ARw1Dgf84XCG");
/**
 * Switchboard Mainnet Program ID
 * SW1TCH7qEPTdLsDHRgPuMQjbQxKdH2aBStViMFnt64f
 */
SwitchboardProgram.mainnetPid = new anchor.web3.PublicKey("SW1TCH7qEPTdLsDHRgPuMQjbQxKdH2aBStViMFnt64f");
/**
 * Default confirmation options for fetching Solana data
 */
SwitchboardProgram.defaultConfirmOptions = {
    commitment: "confirmed",
};
//# sourceMappingURL=index.js.map